// document — joindre un PDF, un .docx ou du texte, et n'envoyer que du TEXTE.
//
// Demande de l'opérateur, le 2026-10-05 : « we want to be able to upload photos but also
// documents ok pdf txt docx ».
//
// ── LA DÉCISION QUI TIENT TOUT ──────────────────────────────────────────────
//
// Le navigateur extrait le texte AVANT d'envoyer. Ce qui part n'est pas un fichier, c'est du
// texte, et c'est pour ces quatre conséquences :
//
//   • aucun modèle n'a besoin de « savoir lire » un PDF — du texte, tout le monde lit ;
//   • le texte vit dans le préfixe mis en cache du fournisseur : payé une fois, pas à chaque
//     tour (entrée $0,0030/M contre $2,40/M en sortie, prix publiés relevés le 2026-10-05) ;
//   • les DEUX voies l'ont, sans un octet de transport nouveau ;
//   • rien n'est re-parsé à chaque appel.
//
// L'autre voie — confier le fichier au fournisseur — a été écartée sur deux FAITS lus dans sa
// documentation le 2026-10-05 : elle ne couvre PAS le .docx, et son moteur par défaut est un
// OCR payant.
//
// ── CE MODULE NE LIT AUCUN FICHIER ──────────────────────────────────────────
//
// Tout ici est pur : ce qu'on accepte, les bornes, les refus, la lecture d'un ZIP sur des
// octets qu'on lui donne, et la traduction du XML de Word. L'ouverture des fichiers vit dans
// src/lib/document_read.ts, qui n'a plus aucune règle à décider.

/** Combien de documents par message. Trois : de quoi comparer un formulaire, sa liste et sa
 *  consigne, sans ouvrir la porte à dix. Le serveur tient la même borne (lib/document_intake,
 *  et c'est LUI qui fait foi). */
export const DOC_MAX_FILES = 3;

/** Le budget de texte TOTAL, en caractères, pour les documents d'un message.
 *
 *  POURQUOI EN CARACTÈRES : un caractère se compte exactement, un jeton s'estime — et la
 *  borne reste vraie dans le bon sens, puisqu'un jeton vaut au moins un caractère. 48 000
 *  caractères ≈ 16 pages, ce que l'opérateur a validé (« ~15 pages »). */
export const DOC_MAX_CHARS = 48_000;

/** Le poids d'un fichier qu'on accepte d'OUVRIR, avant toute extraction. Il ne borne pas le
 *  coût (c'est le travail de DOC_MAX_CHARS) : il empêche de charger 500 Mo dans l'onglet de
 *  quelqu'un pour en jeter 99 %. */
export const DOC_MAX_BYTES = 20_000_000;

/** Ce que le sélecteur de fichiers propose. Les types ET les extensions : un navigateur rend
 *  souvent `type: ""` pour un .csv ou un .md, et l'extension est alors la seule prise. */
export const DOC_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json",
  ".pdf", ".docx", ".txt", ".csv", ".md", ".json",
] as const;

export type DocumentKind = "text" | "docx" | "pdf";

const PAR_TYPE: ReadonlyMap<string, DocumentKind> = new Map([
  ["application/pdf", "pdf"],
  ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", "docx"],
  ["text/plain", "text"],
  ["text/csv", "text"],
  ["text/markdown", "text"],
  ["application/json", "text"],
]);

const PAR_EXTENSION: ReadonlyMap<string, DocumentKind> = new Map([
  ["pdf", "pdf"], ["docx", "docx"],
  ["txt", "text"], ["csv", "text"], ["md", "text"], ["json", "text"],
]);

/**
 * PURE. Comment ouvrir ce fichier, ou `null` si on ne sait pas.
 *
 * LE TYPE D'ABORD, L'EXTENSION ENSUITE. Un navigateur rend régulièrement `type: ""` pour un
 * .csv ou un .md ; juger sur le seul type refuserait des fichiers parfaitement lisibles. Et
 * FERMÉ dans les deux cas : un .doc ancien n'est pas un zip, un .xlsx a une autre structure
 * interne — les accepter donnerait du charabia au modèle.
 */
export function documentKind(mime: string, name: string): DocumentKind | null {
  const parType = PAR_TYPE.get(mime.trim().toLowerCase());
  if (parType !== undefined) return parType;
  const point = name.lastIndexOf(".");
  if (point < 0) return null;
  return PAR_EXTENSION.get(name.slice(point + 1).toLowerCase()) ?? null;
}

export type DocumentRefusal = "too-many" | "bad-type" | "too-big" | "no-text" | "unreadable" | "off";

/** PURE. Pourquoi le document n'est pas parti, en mots utilisables.
 *
 *  AUCUN NE NOMME UN MODÈLE : un réglage se nomme d'après ce qu'il fait, jamais d'après ce
 *  qu'il emploie. */
export function documentRefusal(reason: DocumentRefusal): string {
  switch (reason) {
    case "too-many":
      return `Up to ${DOC_MAX_FILES} documents per message. Remove one to add another.`;
    case "bad-type":
      return "That file isn't a document we can read. Use PDF, Word (.docx), or a text file (.txt, .csv, .md, .json).";
    case "too-big":
      return `That file is too large to open here — keep it under ${Math.round(DOC_MAX_BYTES / 1_000_000)} MB.`;
    case "no-text":
      // LA porte de sortie, et elle existe vraiment : les images marchent déjà.
      return "No text could be read from that file. If it is a scan, take a photo of the page and attach that instead.";
    case "unreadable":
      return "That file could not be opened. Try saving it again, or copy the part you need into the message.";
    case "off":
      return "Attachments are part of the economy mode for your platform. Switch it on in settings to attach a document.";
    default:
      return "That document could not be added. Try another file.";
  }
}

// ── LIRE UN ZIP (UN .docx EN EST UN) ────────────────────────────────────────
//
// Décalages relevés sur la spécification du format ZIP le 2026-10-05, et éprouvés contre des
// archives FABRIQUÉES à la spécification dans document.test.ts — pas contre une structure
// imaginée.
//
// On passe par l'ANNUAIRE CENTRAL et non par un balayage des en-têtes locaux : c'est
// l'annuaire qui fait foi dans un ZIP, et chercher « word/document.xml » à vue tomberait sur
// « word/_rels/document.xml.rels », qui le contient.

const SIG_EOCD = 0x06054b50;
const SIG_CD   = 0x02014b50;
const SIG_LOCAL = 0x04034b50;

/** La fin de l'annuaire central peut être suivie d'un commentaire de 65 535 octets au plus. */
const COMMENTAIRE_MAX = 0xffff;

const u16 = (b: Uint8Array, o: number): number => b[o]! | (b[o + 1]! << 8);
const u32 = (b: Uint8Array, o: number): number =>
  (b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16)) + b[o + 3]! * 0x1000000;

export type ZipFile = {
  /** Les octets de l'entrée, tels qu'ils sont stockés. */
  readonly data: Uint8Array;
  /** 0 = tel quel, 8 = `deflate-raw`. L'appelant décompresse ; ce module ne fait pas de flux. */
  readonly method: number;
};

/**
 * PURE. Les octets d'une entrée nommée dans un ZIP, ou `null`.
 *
 * NE LÈVE JAMAIS. Un fichier renommé en .docx, un téléversement tronqué, un ZIP qui annonce
 * une taille impossible : tout cela arrive dans l'onglet de quelqu'un, et doit devenir un
 * refus qui se lit, jamais une erreur qui casse l'écran.
 */
export function zipFileBytes(bytes: Uint8Array, name: string): ZipFile | null {
  // 1. La fin de l'annuaire central, cherchée à reculons.
  const minimum = 22;
  if (bytes.length < minimum) return null;
  let eocd = -1;
  const plancher = Math.max(0, bytes.length - minimum - COMMENTAIRE_MAX);
  for (let i = bytes.length - minimum; i >= plancher; i--) {
    if (u32(bytes, i) === SIG_EOCD) { eocd = i; break; }
  }
  if (eocd < 0) return null;

  const nombre = u16(bytes, eocd + 10);
  const debutCd = u32(bytes, eocd + 16);
  if (debutCd >= bytes.length) return null;

  // 2. L'annuaire, entrée par entrée, jusqu'au nom EXACT.
  const cible = new TextEncoder().encode(name);
  let p = debutCd;
  for (let n = 0; n < nombre; n++) {
    if (p + 46 > bytes.length || u32(bytes, p) !== SIG_CD) return null;
    const tailleNom = u16(bytes, p + 28);
    const tailleExtra = u16(bytes, p + 30);
    const tailleCommentaire = u16(bytes, p + 32);
    const finNom = p + 46 + tailleNom;
    if (finNom > bytes.length) return null;
    let pareil = tailleNom === cible.length;
    for (let k = 0; pareil && k < tailleNom; k++) pareil = bytes[p + 46 + k] === cible[k];
    if (pareil) {
      const methode = u16(bytes, p + 10);
      const taille = u32(bytes, p + 20);
      const local = u32(bytes, p + 42);
      // 3. L'en-tête local donne où commencent VRAIMENT les octets : la longueur de son champ
      //    « extra » peut différer de celle de l'annuaire, et s'en passer décalerait la lecture.
      if (local + 30 > bytes.length || u32(bytes, local) !== SIG_LOCAL) return null;
      const debut = local + 30 + u16(bytes, local + 26) + u16(bytes, local + 28);
      // Une taille annoncée qui dépasse le fichier : on refuse, on ne lit pas hors bornes.
      if (debut + taille > bytes.length) return null;
      return { data: bytes.subarray(debut, debut + taille), method: methode };
    }
    p = finNom + tailleExtra + tailleCommentaire;
  }
  return null;
}

/** PURE. Le nettoyage des blancs, commun aux trois extracteurs.
 *
 *  POURQUOI IL EST PARTAGÉ : il était écrit deux fois, à une ligne près, dans la lecture du
 *  .docx et dans celle du PDF. Une mutation me l'a montré — son ancre apparaissait DEUX fois
 *  dans le fichier. Le jour où l'une des deux change, deux formats identiques à l'écran
 *  partent avec des blancs différents, et c'est le cache du fournisseur qui le paie.
 *
 *  Ce qu'il fait : les espaces en fin de ligne partent, et une rafale de lignes vides est
 *  réduite à DEUX — un .docx en porte des dizaines, et chaque caractère envoyé se paie, mais
 *  une séparation de sections reste lisible. */
const réduireBlancs = (s: string): string =>
  s.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();

// ── LE XML DE WORD → DU TEXTE ───────────────────────────────────────────────

/** Les entités XML que Word écrit. Le reste passe par la forme numérique. */
const ENTITES: ReadonlyMap<string, string> = new Map([
  ["amp", "&"], ["lt", "<"], ["gt", ">"], ["quot", "\""], ["apos", "'"],
]);

const décoder = (s: string): string =>
  s.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (tout, corps: string) => {
    if (corps.startsWith("#")) {
      const n = corps[1] === "x" || corps[1] === "X"
        ? parseInt(corps.slice(2), 16)
        : parseInt(corps.slice(1), 10);
      return Number.isFinite(n) && n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : tout;
    }
    return ENTITES.get(corps) ?? tout;
  });

/**
 * PURE. Le texte VISIBLE d'un `word/document.xml`.
 *
 * ── POURQUOI ON NE PREND PAS « TOUT LE TEXTE » ──────────────────────────────
 *
 * Retirer les balises et garder le reste ferait sortir `w:instrText` — un code de champ, du
 * genre « MERGEFIELD Nom » — et `w:delText`, un passage SUPPRIMÉ en révision. Le modèle
 * lirait alors un document qui ne ressemble pas à celui que l'utilisateur voit à l'écran. On
 * ne garde donc que `w:t`, qui est le texte affiché, et on traduit les séparateurs de Word.
 *
 * NE LÈVE JAMAIS : un XML tronqué ou qui n'en est pas rend une chaîne vide, et l'appelant
 * répond « aucun texte lisible » plutôt que de casser l'écran.
 */
export function docxBodyText(xml: string): string {
  if (typeof xml !== "string" || xml === "") return "";
  let out = "";
  // UN SEUL BALAYAGE, dans l'ordre du document : les séparateurs de Word (fin de paragraphe,
  // tabulation, saut de ligne) comptent autant que le texte, et un formulaire est souvent un
  // tableau — sans tabulations « Nom Jean » devient « NomJean » et les colonnes disparaissent.
  const jeton = /<w:t(?:\s[^>]*)?>([\s\S]*?)<\/w:t>|<w:t\s*\/>|<w:tab\s*\/>|<w:br\s*\/>|<\/w:p>|<w:p\s*\/>/g;
  for (let m = jeton.exec(xml); m !== null; m = jeton.exec(xml)) {
    const [tout, texte] = m;
    if (texte !== undefined) out += décoder(texte);
    else if (tout.startsWith("<w:tab")) out += "\t";
    else if (tout.startsWith("<w:br")) out += "\n";
    else if (tout.startsWith("</w:p") || tout.startsWith("<w:p")) out += "\n";
  }
  // LES RAFALES DE LIGNES VIDES SONT RÉDUITES : un .docx en porte des dizaines, et chaque
  // caractère envoyé se paie. Deux au plus, pour garder la séparation des sections.
  return réduireBlancs(out);
}

// ── LE PDF : DES MORCEAUX POSITIONNÉS, PAS DU TEXTE ─────────────────────────
//
// MESURÉ le 2026-10-05, sur un PDF témoin fabriqué pour ce test et lu par la librairie :
// `getTextContent()` rend des éléments `{ str, hasEOL, dir, width }`. C'est `hasEOL` qui dit
// où une ligne s'arrête — sans lui tout le document arrive sur UNE ligne, un formulaire
// devient « Nom du salarieSalaire mensuel » et ses champs disparaissent.
//
// L'assemblage est ici, en PUR, parce que c'est lui qui se trompe en silence. Le chargement
// de la librairie vit dans src/lib/document_read.ts.

/** Un élément de texte tel que la librairie le rend. Les champs sont déclarés `unknown` :
 *  ils viennent d'une librairie, et un élément de marquage sans `str` existe. */
export type PdfTextItem = { readonly str?: unknown; readonly hasEOL?: unknown };

/** PURE. Le texte d'UNE page, depuis ses morceaux. */
export function pdfItemsToText(items: readonly PdfTextItem[]): string {
  let out = "";
  for (const it of items) {
    if (typeof it?.str === "string") out += it.str;
    if (it?.hasEOL === true) out += "\n";
  }
  return réduireBlancs(out);
}

/** PURE. Le texte d'un document, depuis ses pages.
 *
 *  UNE PAGE VIDE N'AJOUTE RIEN : une page d'illustration ou de garde est fréquente, et des
 *  lignes blanches à sa place se paieraient en jetons pour ne rien dire. */
export function pdfPagesToText(pages: readonly string[]): string {
  return pages.map((p) => p.trim()).filter((p) => p !== "").join("\n\n");
}

/** Combien de caractères valent une page, pour PARLER à l'utilisateur. Personne ne compte en
 *  caractères ; 3 000 est l'ordre de grandeur d'une page dense. Estimation assumée, elle ne
 *  décide de rien. */
const CARACTERES_PAR_PAGE = 3_000;

/**
 * PURE. L'avertissement à montrer quand les documents dépassent le budget, ou `null`.
 *
 * ON AVERTIT, ON NE REFUSE PAS. Le serveur coupe au budget et le DIT au modèle
 * (platform-service/lib/attached_documents_brief). Refuser ici rendrait un PDF de trente
 * pages inutilisable alors que ses seize premières sont peut-être exactement ce qu'il
 * fallait lire. On informe, l'utilisateur décide.
 */
export function documentBudgetNote(totalChars: number): string | null {
  if (!Number.isFinite(totalChars) || totalChars <= DOC_MAX_CHARS) return null;
  const pages = Math.floor(DOC_MAX_CHARS / CARACTERES_PAR_PAGE);
  return `That is more text than one message carries — about the first ${pages} pages will be sent, and the agent is told the rest was cut.`;
}
